/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description: 图片选区编辑器组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-08-26 14:51:17
 */
import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle
} from 'react'
import {
  Stage,
  Layer,
  Image as KonvaImage,
  Rect,
  Transformer,
  Text
} from 'react-konva'
import Konva from 'konva'
import useImage from 'use-image'
import { convertComponentToOriginalCoords } from '../../../utils/imageUtils'

interface XCSelectionInfo {
  x: number
  y: number
  width: number
  height: number
}

interface ImageSelectionEditorProps {
  /**
   * 图片源地址
   * 图片源地址，支持网络图片和本地图片
   */
  src: string
  /**
   * 图片宽度
   * 图片宽度，默认800
   */
  width?: number
  /**
   * 图片高度
   * 图片高度，默认600
   */
  height?: number
  /**
   * 是否已有选区，用于控制初始状态
   * 是否已有选区，默认false
   */
  hasSelection?: boolean
  /**
   * 初始选区数组
   * 基于组件显示尺寸的选区坐标，用于设置初始选区
   */
  initialSelections?: XCSelectionInfo[]
  /**
   * 最小选区宽度
   * 最小选区宽度，默认30
   */
  minSelectionWidth?: number
  /**
   * 最小选区高度
   * 最小选区高度，默认30
   */
  minSelectionHeight?: number
  /**
   * 选择类型：单选或多选
   * 默认单选，当值为单选时，不支持创建新的选区
   */
  selectionType?: 'single' | 'multiple'
  /**
   * 选区变化回调函数，返回基于原始图片尺寸的坐标信息
   * 不管单选还是多选模式都返回选区数组，空选区时返回空数组
   */
  onSelectionChange?: (selectionInfo: XCSelectionInfo[]) => void
  /**
   * 确认选区回调函数，返回最终选区信息
   * 不管单选还是多选模式都返回选区数组
   */
  onConfirm?: (selectionInfo: XCSelectionInfo[]) => void
  /**
   * 是否禁用交互功能
   * 当设置为true时，组件仅用于展示，不允许用户修改选区
   */
  disabled?: boolean
}

interface XCImageSelectionEditorRef {
  triggerAnimation: () => void
  setSelections: (selections: XCSelectionInfo[]) => void
}

interface SelectionRectangle {
  visible: boolean
  x: number
  y: number
  width: number
  height: number
}

const ImageSelectionEditor = forwardRef<
  XCImageSelectionEditorRef,
  ImageSelectionEditorProps
>(
  (
    {
      src,
      width = 800,
      height = 600,
      initialSelections,
      minSelectionWidth = 30,
      minSelectionHeight = 30,
      onSelectionChange,
      disabled = false
    },
    ref
  ) => {
    const stageRef = useRef<Konva.Stage>(null)
    const layerRef = useRef<Konva.Layer>(null)
    const transformerRef = useRef<Konva.Transformer>(null)
    const selectionRectRef = useRef<Konva.Rect>(null)

    const [image] = useImage(src)
    const [selectedNodes, setSelectedNodes] = useState<Konva.Node[]>([])
    const [rectangles, setRectangles] = useState<any[]>([])
    const [selectionRect, setSelectionRect] = useState<SelectionRectangle>({
      visible: false,
      x: 0,
      y: 0,
      width: 0,
      height: 0
    })
    // 控制是否显示选区矩形
    const [hasRectMoved, setHasRectMoved] = useState(false)

    // 控制是否显示呼吸动画
    const [isBlinking, setIsBlinking] = useState(false)

    // 控制是否正在拖动或移动
    const [isDragOrMove, setIsDragOrMove] = useState(false)

    // 使用 ref 来跟踪是否已经初始化
    const isInitializedRef = useRef(false)
    const blinkAnimationRef = useRef<Konva.Tween | null>(null)
    // 记录之前的尺寸用于比例缩放
    const prevSizeRef = useRef({ width, height })

    // 恢复矩形原始样式的函数
    const restoreRectOriginalStyle = useCallback((rectNode: Konva.Node) => {
      const rect = rectNode as Konva.Rect
      rect.scaleX(1)
      rect.scaleY(1)
      rect.stroke('#FF0000')
      rect.strokeWidth(1)
      rect.shadowColor('rgba(0, 0, 0, 0.2)')
      rect.shadowBlur(4)
      void rectNode.getLayer()?.batchDraw()
    }, [])

    // 呼吸动画函数
    const startBlinkAnimation = useCallback(
      (rectNode: Konva.Node) => {
        if (!rectNode || isBlinking) return

        setIsBlinking(true)
        let animationCount = 0
        const maxAnimations = 2 // 执行2次呼吸动画

        const rect = rectNode as Konva.Rect
        // 保存原始样式
        const originalScaleX = rect.scaleX() || 1
        const originalScaleY = rect.scaleY() || 1
        const originalStroke = rect.stroke()
        const originalStrokeWidth = rect.strokeWidth()

        const breatheAnimation = () => {
          if (animationCount >= maxAnimations) {
            setIsBlinking(false)
            // 恢复原始样式
            rect.scaleX(originalScaleX)
            rect.scaleY(originalScaleY)
            rect.stroke(originalStroke)
            rect.strokeWidth(originalStrokeWidth)
            rect.shadowColor('rgba(0, 0, 0, 0.2)')
            rect.shadowBlur(4)
            void rectNode.getLayer()?.batchDraw()
            return
          }

          // 放大动画（吸气）
          const expandTween = new Konva.Tween({
            node: rectNode,
            duration: 0.3,
            scaleX: originalScaleX * 1.1,
            scaleY: originalScaleY * 1.1,
            stroke: '#FF6B35', // 醒目的橙色
            strokeWidth: 3,
            shadowColor: 'rgba(255, 107, 53, 0.6)',
            shadowBlur: 12,
            easing: Konva.Easings.EaseInOut,
            onFinish: () => {
              // 缩小动画（呼气）
              const contractTween = new Konva.Tween({
                node: rectNode,
                duration: 0.3,
                scaleX: originalScaleX,
                scaleY: originalScaleY,
                stroke: '#FF0000',
                strokeWidth: 1,
                shadowColor: 'rgba(0, 0, 0, 0.2)',
                shadowBlur: 4,
                easing: Konva.Easings.EaseInOut,
                onFinish: () => {
                  animationCount++
                  setTimeout(breatheAnimation, 100) // 短暂停顿后继续下一次呼吸
                }
              })
              blinkAnimationRef.current = contractTween
              contractTween.play()
            }
          })

          blinkAnimationRef.current = expandTween
          expandTween.play()
        }

        // 延迟开始动画，确保矩形已完全渲染
        setTimeout(breatheAnimation, 500)
      },
      [isBlinking, restoreRectOriginalStyle]
    )

    // 组件加载时创建矩形（初始选区或默认矩形）
    useEffect(() => {
      if (image && !isInitializedRef.current) {
        if (initialSelections && initialSelections.length > 0) {
          // 使用初始选区
          const initialRects = initialSelections.map((selection, index) => ({
            id: `initial-rect-${index}`,
            x: selection.x,
            y: selection.y,
            width: selection.width,
            height: selection.height,
            fill: 'rgba(255, 0, 0, 0.15)',
            stroke: '#FF0000',
            strokeWidth: 1
          }))
          setRectangles(initialRects)
        } else {
          // 创建默认矩形
          const defaultRect = {
            id: 'default-rect',
            x: width / 2 - 150,
            y: height / 2 - 100,
            width: 300,
            height: 200,
            fill: 'rgba(255, 0, 0, 0.15)',
            stroke: '#FF0000',
            strokeWidth: 1
          }
          setRectangles([defaultRect])
        }
        isInitializedRef.current = true

        // 延迟选中矩形和执行动画（仅在非disabled状态下）
        if (!disabled) {
          setTimeout(() => {
            const stage = stageRef.current
            if (stage) {
              const rectNode =
                initialSelections && initialSelections.length > 0
                  ? stage.findOne('#initial-rect-0')
                  : stage.findOne('#default-rect')
              if (rectNode) {
                setSelectedNodes([rectNode])
                // 启动闪烁动画提醒用户选区支持缩放
                startBlinkAnimation(rectNode)
              }
            }
          }, 100)
        }
      }
    }, [image, width, height, initialSelections, startBlinkAnimation, disabled])

    // 监听width和height变化，按比例缩放现有矩形
    useEffect(() => {
      const prevSize = prevSizeRef.current
      if (
        rectangles.length > 0 &&
        (prevSize.width !== width || prevSize.height !== height)
      ) {
        const scaleX = width / prevSize.width
        const scaleY = height / prevSize.height

        setRectangles((prevRectangles) =>
          prevRectangles.map((rect) => ({
            ...rect,
            x: rect.x * scaleX,
            y: rect.y * scaleY,
            width: rect.width * scaleX,
            height: rect.height * scaleY
          }))
        )

        // 更新记录的尺寸
        prevSizeRef.current = { width, height }
      } else if (rectangles.length === 0) {
        // 如果没有矩形，只更新记录的尺寸
        prevSizeRef.current = { width, height }
      }
    }, [width, height, rectangles.length])

    // 清理动画
    useEffect(() => {
      return () => {
        if (blinkAnimationRef.current) {
          blinkAnimationRef.current.destroy()
          blinkAnimationRef.current = null
        }
      }
    }, [])

    // 设置选区的方法
    const setSelections = useCallback((selections: XCSelectionInfo[]) => {
      if (!selections || selections.length === 0) {
        setRectangles([])
        setSelectedNodes([])
        return
      }

      const newRectangles = selections.map((selection, index) => ({
        id: `selection-rect-${index}`,
        x: selection.x,
        y: selection.y,
        width: selection.width,
        height: selection.height,
        fill: 'rgba(255, 0, 0, 0.15)',
        stroke: '#FF0000',
        strokeWidth: 1
      }))

      setRectangles(newRectangles)
      isInitializedRef.current = true
    }, [])

    // 暴露给外部的方法
    useImperativeHandle(
      ref,
      () => ({
        triggerAnimation: () => {
          const stage = stageRef.current
          if (stage) {
            // 查找当前选中的矩形或默认矩形
            let targetNode = selectedNodes.length > 0 ? selectedNodes[0] : null
            if (!targetNode) {
              targetNode = stage.findOne('#default-rect')
            }
            if (targetNode) {
              startBlinkAnimation(targetNode)
            }
          }
        },
        setSelections
      }),
      [selectedNodes, startBlinkAnimation, setSelections]
    )

    // 选择框拖拽状态
    const [isDragging, setIsDragging] = useState(false)
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 })

    // 更新Transformer选中的节点
    useEffect(() => {
      if (transformerRef.current) {
        // 设置transformer的选中节点
        transformerRef.current.nodes(selectedNodes)
        const layer = transformerRef.current.getLayer()
        if (layer) {
          // 重新绘制图层
          layer.batchDraw()
        }
      }
    }, [selectedNodes])

    // 处理舞台点击事件
    const handleStageClick = useCallback(
      (e: Konva.KonvaEventObject<MouseEvent>) => {
        if (disabled) return // 禁用状态下不处理交互

        try {
          // 如果正在拖拽选择框，不处理点击
          if (isDragging) return

          const clickedOnEmpty = e.target === e.target.getStage()
          const clickedOnTransformer =
            e.target.getParent()?.className === 'Transformer'

          // 点击空白区域或Transformer，清除选择
          if (clickedOnEmpty || clickedOnTransformer) {
            setSelectedNodes([])
            return
          }

          const clickedNode = e.target
          if (!clickedNode) return

          // 只处理可选择的节点（name为'selectable'的矩形）
          if (clickedNode.name() !== 'selectable') {
            return
          }

          const isSelected = selectedNodes.includes(clickedNode)
          const metaPressed = e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey

          // 防止双击时的快速状态变化
          if (e.evt.detail > 1) {
            // 双击或多击，忽略处理
            return
          }

          if (!metaPressed && !isSelected) {
            // 单击选择单个节点
            setSelectedNodes([clickedNode])
          } else if (metaPressed && isSelected) {
            // Shift+点击已选中的节点，取消选择
            setSelectedNodes(
              selectedNodes.filter((node) => node !== clickedNode)
            )
          } else if (metaPressed && !isSelected) {
            // Shift+点击未选中的节点，添加到选择
            setSelectedNodes([...selectedNodes, clickedNode])
          }
        } catch (error) {
          console.error('handleStageClick error:', error)
        }
      },
      [disabled, selectedNodes, isDragging]
    )

    // 处理鼠标按下事件 - 开始框选
    const handleMouseDown = useCallback(
      (e: Konva.KonvaEventObject<MouseEvent>) => {
        if (disabled) return // 禁用状态下不处理交互

        const clickedOnEmpty = e.target === e.target.getStage()
        if (!clickedOnEmpty) return

        const stage = e.target.getStage()
        if (!stage) return

        const pos = stage.getPointerPosition()
        if (!pos) return

        setIsDragging(true)
        setDragStart({ x: pos.x, y: pos.y })
        setSelectionRect({
          visible: true,
          x: pos.x,
          y: pos.y,
          width: 0,
          height: 0
        })
      },
      [disabled]
    )

    // 处理鼠标移动事件 - 更新框选区域
    const handleMouseMove = useCallback(
      (e: Konva.KonvaEventObject<MouseEvent>) => {
        if (disabled || !isDragging) return // 禁用状态下不处理交互

        const stage = e.target.getStage()
        if (!stage) return

        const pos = stage.getPointerPosition()
        if (!pos) return

        setSelectionRect({
          visible: true,
          x: Math.min(dragStart.x, pos.x),
          y: Math.min(dragStart.y, pos.y),
          width: Math.abs(pos.x - dragStart.x),
          height: Math.abs(pos.y - dragStart.y)
        })
      },
      [disabled, isDragging, dragStart]
    )

    // 处理鼠标释放事件 - 完成框选
    const handleMouseUp = useCallback(() => {
      if (disabled || !isDragging) return // 禁用状态下不处理交互

      setIsDragging(false)

      // 获取选择框的边界
      const selectionBox = {
        x: selectionRect.x,
        y: selectionRect.y,
        width: selectionRect.width,
        height: selectionRect.height
      }

      // 检查选区是否满足最小尺寸要求
      if (
        selectionBox.width < minSelectionWidth ||
        selectionBox.height < minSelectionHeight
      ) {
        // 如果不满足最小尺寸，直接隐藏选择框，不创建选区
        setSelectionRect((prev) => ({ ...prev, visible: false }))
        return
      }

      // 找到与选择框相交的所有矩形
      const layer = layerRef.current
      if (layer) {
        const shapes = layer.find('.selectable')
        const selected = shapes.toArray().filter((shape: Konva.Node) => {
          const shapeBox = shape.getClientRect()
          return Konva.Util.haveIntersection(selectionBox, shapeBox)
        })

        setSelectedNodes(selected)
      }

      // 隐藏选择框
      setSelectionRect((prev) => ({ ...prev, visible: false }))
    }, [
      disabled,
      isDragging,
      selectionRect,
      minSelectionWidth,
      minSelectionHeight
    ])

    // 删除选中的节点
    const handleDelete = useCallback(
      (e: KeyboardEvent) => {
        if (disabled) return // 禁用状态下不处理交互

        if (e.key === 'Delete' || e.key === 'Backspace') {
          if (selectedNodes.length > 0) {
            const selectedIds = selectedNodes.map((node) => node.id())
            setRectangles(
              rectangles.filter((rect) => !selectedIds.includes(rect.id))
            )
            setSelectedNodes([])
          }
        }
      },
      [disabled, selectedNodes, rectangles]
    )

    // 处理矩形拖拽结束事件
    const handleRectDragEnd = useCallback(
      (e: Konva.KonvaEventObject<DragEvent>) => {
        if (disabled) return // 禁用状态下不处理交互

        const rect = e.target
        const id = rect.id()
        const newX = rect.x()
        const newY = rect.y()

        // 停止动画并恢复原始样式
        if (blinkAnimationRef.current) {
          blinkAnimationRef.current.destroy()
          blinkAnimationRef.current = null
        }
        setIsBlinking(false)
        restoreRectOriginalStyle(rect)

        // 标记矩形已被移动
        setHasRectMoved(true)
        // 重置拖动状态
        setIsDragOrMove(false)

        // 如果当前矩形没有被选中，则选中它
        if (!selectedNodes.includes(rect)) {
          setSelectedNodes([rect])
        }

        // 更新矩形位置状态
        setRectangles((prevRects) =>
          prevRects.map((r) => (r.id === id ? { ...r, x: newX, y: newY } : r))
        )

        // 通知父组件选区位置变化（仅在拖动移动时回调）
        if (onSelectionChange && image) {
          const canvasRect = {
            x: newX,
            y: newY,
            width: rect.width() * rect.scaleX(),
            height: rect.height() * rect.scaleY()
          }

          // 转换为基于原始图片尺寸的坐标
          const originalCoords = convertComponentToOriginalCoords(
            canvasRect,
            { width, height }, // 显示尺寸
            { width: image.width, height: image.height } // 原始尺寸
          )

          // 将单个对象包装成数组格式以支持单选和多选
          onSelectionChange([originalCoords])
        }
      },
      [
        disabled,
        onSelectionChange,
        selectedNodes,
        image,
        width,
        height,
        restoreRectOriginalStyle
      ]
    )

    // 处理Transformer变换结束事件
    const handleTransformEnd = useCallback(() => {
      if (disabled) return // 禁用状态下不处理交互

      if (selectedNodes.length === 1) {
        const node = selectedNodes[0]
        const id = node.id()
        const newX = node.x()
        const newY = node.y()
        const newWidth = node.width() * node.scaleX()
        const newHeight = node.height() * node.scaleY()

        // 停止动画并恢复原始样式
        if (blinkAnimationRef.current) {
          blinkAnimationRef.current.destroy()
          blinkAnimationRef.current = null
        }
        setIsBlinking(false)
        restoreRectOriginalStyle(node)

        // 标记矩形已被移动
        setHasRectMoved(true)
        // 重置拖动状态
        setIsDragOrMove(false)

        // 重置缩放比例
        node.scaleX(1)
        node.scaleY(1)
        node.width(newWidth)
        node.height(newHeight)

        // 更新矩形状态
        setRectangles((prevRects) =>
          prevRects.map((r) =>
            r.id === id
              ? { ...r, x: newX, y: newY, width: newWidth, height: newHeight }
              : r
          )
        )

        // 通知父组件选区尺寸变化（缩放结束时回调）
        if (onSelectionChange && image) {
          const canvasRect = {
            x: newX,
            y: newY,
            width: newWidth,
            height: newHeight
          }

          // 转换为基于原始图片尺寸的坐标
          const originalCoords = convertComponentToOriginalCoords(
            canvasRect,
            { width, height }, // 显示尺寸
            { width: image.width, height: image.height } // 原始尺寸
          )

          // 将单个对象包装成数组格式以支持单选和多选
          onSelectionChange([originalCoords])
        }
      }
    }, [disabled, selectedNodes, onSelectionChange, image, width, height])

    // 监听键盘事件
    useEffect(() => {
      window.addEventListener('keydown', handleDelete)
      return () => {
        window.removeEventListener('keydown', handleDelete)
      }
    }, [handleDelete])

    return (
      <div
        style={{
          border: '1px solid #e1e5e9',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          backgroundColor: '#ffffff'
        }}
      >
        <Stage
          ref={stageRef}
          width={width}
          height={height}
          onClick={handleStageClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          <Layer ref={layerRef}>
            {/* 背景图片 */}
            {image && (
              <KonvaImage
                image={image}
                width={width}
                height={height}
                listening={false}
              />
            )}

            {/* 可选择的矩形 */}
            {rectangles.map((rect) => (
              <React.Fragment key={rect.id}>
                <Rect
                  id={rect.id}
                  name="selectable"
                  x={rect.x}
                  y={rect.y}
                  width={rect.width}
                  height={rect.height}
                  fill={rect.fill || 'rgba(255, 0, 0, 1)'}
                  stroke={rect.stroke || '#FF0000'}
                  strokeWidth={1}
                  draggable={!disabled}
                  dragBoundFunc={(pos) => {
                    // 限制矩形拖拽不能超出图片边界
                    const newX = Math.max(
                      0,
                      Math.min(pos.x, width - rect.width)
                    )
                    const newY = Math.max(
                      0,
                      Math.min(pos.y, height - rect.height)
                    )
                    return { x: newX, y: newY }
                  }}
                  onDragStart={() => setIsDragOrMove(true)}
                  onDragEnd={handleRectDragEnd}
                  onClick={(e) => {
                    if (!disabled) {
                      const rect = e.target
                      // 如果当前矩形没有被选中，则选中它
                      if (!selectedNodes.includes(rect)) {
                        setSelectedNodes([rect])
                        setHasRectMoved(true)
                      }
                    }
                  }}
                  shadowColor="rgba(0, 0, 0, 0.2)"
                  shadowBlur={4}
                  shadowOffset={{ x: 2, y: 2 }}
                  shadowOpacity={0.3}
                  listening={!disabled}
                />
                {/* 选区中间的提示文案 - 仅在非disabled状态下显示 */}
                {!disabled && !isDragOrMove && !hasRectMoved && (
                  <Text
                    x={rect.x + rect.width / 2}
                    y={rect.y + rect.height / 2}
                    text="拖拽选择局部区域，准确度更高"
                    fontSize={14}
                    fontFamily="PingFang SC, Arial, sans-serif"
                    fill="#FFFFFF"
                    align="center"
                    verticalAlign="middle"
                    offsetX={90} // 文字宽度的一半，用于居中
                    offsetY={7} // 文字高度的一半，用于居中
                    listening={false}
                  />
                )}
                {/* 已选择状态的提示文案 - 仅在非disabled状态下显示 */}
                {!disabled && !isDragOrMove && hasRectMoved && (
                  <Text
                    x={rect.x + rect.width / 2}
                    y={rect.y + rect.height / 2}
                    text={`已选择识别区域`}
                    fontSize={14}
                    fontFamily="PingFang SC, Arial, sans-serif"
                    fill="#FFFFFF"
                    align="center"
                    verticalAlign="middle"
                    offsetX={45} // 文字宽度的一半，用于居中
                    offsetY={7} // 文字高度的一半，用于居中
                    listening={false}
                  />
                )}
              </React.Fragment>
            ))}

            {/* 选择框 */}
            {!disabled && selectionRect.visible && (
              <Rect
                ref={selectionRectRef}
                x={selectionRect.x}
                y={selectionRect.y}
                width={selectionRect.width}
                height={selectionRect.height}
                fill="rgba(255, 0, 0, 0.08)"
                stroke="#FF0000"
                strokeWidth={1}
                dash={[8, 4]}
                listening={false}
                shadowColor="rgba(255, 0, 0, 0.3)"
                shadowBlur={6}
                shadowOpacity={0.5}
              />
            )}

            {/* Transformer用于缩放 - 禁用旋转功能 */}
            {!disabled && (
              <Transformer
                ref={transformerRef}
                boundBoxFunc={(oldBox, newBox) => {
                  // 限制最小尺寸
                  if (
                    newBox.width < minSelectionWidth ||
                    newBox.height < minSelectionHeight
                  ) {
                    return oldBox
                  }

                  // 限制不能超出图片边界
                  const maxX = width - newBox.width
                  const maxY = height - newBox.height

                  // 确保新位置在边界内
                  const boundedX = Math.max(0, Math.min(newBox.x, maxX))
                  const boundedY = Math.max(0, Math.min(newBox.y, maxY))

                  // 如果位置需要调整，则调整宽高以适应边界
                  let boundedWidth = newBox.width
                  let boundedHeight = newBox.height

                  if (boundedX + boundedWidth > width) {
                    boundedWidth = width - boundedX
                  }
                  if (boundedY + boundedHeight > height) {
                    boundedHeight = height - boundedY
                  }

                  return {
                    ...newBox,
                    x: boundedX,
                    y: boundedY,
                    width: boundedWidth,
                    height: boundedHeight
                  }
                }}
                enabledAnchors={[
                  'top-left',
                  'top-center',
                  'top-right',
                  'middle-right',
                  'bottom-right',
                  'bottom-center',
                  'bottom-left',
                  'middle-left'
                ]}
                rotateEnabled={false} // 旋转功能
                borderEnabled={true}
                borderStroke="#FF0000"
                borderStrokeWidth={1}
                anchorFill="#FF0000"
                anchorStroke="#ffffff"
                anchorStrokeWidth={1}
                anchorSize={10}
                anchorCornerRadius={2}
                keepRatio={false}
                listening={!disabled}
                onTransformStart={() => setIsDragOrMove(true)}
                onTransformEnd={handleTransformEnd}
              />
            )}
          </Layer>
        </Stage>
      </div>
    )
  }
)

ImageSelectionEditor.displayName = 'ImageSelectionEditor'

export default ImageSelectionEditor
export type { XCImageSelectionEditorRef }
